import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Pause, Play, Trash2, ListPlus } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const sel = "rounded-sm border border-stone-300 bg-white px-3 py-1.5 text-sm";

export const QueueBar = ({ selected, clear, reload, founders }) => {
  const [o, setO] = useState({ collection: founders ? "founders" : "researched" });
  if (!selected.length) return null;
  const add = async () => {
    try {
      const { data } = await http.post("/register/queue", { ids: selected, ...o });
      toast.success(`${data.queued} kit(s) added to the build queue${data.skipped ? ` · ${data.skipped} skipped (already built or queued)` : ""}`);
      clear(); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="queue-bar" className="sticky top-0 z-10 mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-stone-300 bg-[#2B2A26] px-4 py-3 text-white">
      <span data-testid="queue-selected-count" className="text-sm font-bold">{selected.length} selected</span>
      <select data-testid="queue-collection" className={`${sel} text-stone-800`} value={o.collection} onChange={(e) => setO({ ...o, collection: e.target.value })}>
        <option value="researched">Researched Kit</option><option value="founders">Founder's Collection</option></select>
      <button data-testid="queue-add-button" onClick={add} className="inline-flex items-center gap-2 rounded-sm bg-[#C9A847] px-4 py-1.5 text-sm font-bold text-[#2B2A26] hover:bg-[#d8b955]"><ListPlus size={14} /> Add to build queue</button>
      <button data-testid="queue-clear-selection" onClick={clear} className="text-xs text-stone-300 hover:text-white ml-auto">Clear selection</button>
    </div>
  );
};

export const QueuePanel = ({ q, reload }) => {
  const [busy, setBusy] = useState(false);
  if (!q || (!q.queued.length && !q.current && !q.paused)) return null;
  const act = async (path) => {
    setBusy(true);
    try { await http.post(`/register/queue/${path}`); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="queue-panel" className="mb-5 rounded-xl border border-[#C9A847]/50 bg-[#FBF7EC] p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="eyebrow">Build queue</div>
        <span data-testid="queue-count" className="text-sm text-stone-600">{q.queued.length} waiting · builds one at a time</span>
        <div className="ml-auto flex gap-2">
          {q.paused
            ? <button data-testid="queue-resume" disabled={busy} onClick={() => act("resume")} className="inline-flex items-center gap-1.5 rounded-sm bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white"><Play size={13} /> Resume</button>
            : <button data-testid="queue-pause" disabled={busy} onClick={() => act("pause")} className="inline-flex items-center gap-1.5 rounded-sm border border-stone-300 px-3 py-1.5 text-xs"><Pause size={13} /> Pause</button>}
          {q.queued.length > 0 && <button data-testid="queue-clear" disabled={busy} onClick={() => window.confirm("Remove every waiting kit from the queue?") && act("clear")} className="inline-flex items-center gap-1.5 rounded-sm border border-stone-300 px-3 py-1.5 text-xs"><Trash2 size={13} /> Clear queue</button>}
        </div>
      </div>
      {q.paused && <div data-testid="queue-paused" className="mt-2 text-sm text-amber-700">Paused — {q.reason || "press Resume to continue"}</div>}
      {q.current && <div data-testid="queue-current" className="mt-2 flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin text-[#9A7B2C]" />
        Building <b>{q.current.destination}</b> · <span className="text-stone-500">{q.current.progress?.current || q.current.status}</span></div>}
      {q.queued.length > 0 && <div className="mt-2 text-xs text-stone-500">Next: {q.queued.slice(0, 5).map((x) => x.destination).join(" → ")}{q.queued.length > 5 ? ` … +${q.queued.length - 5}` : ""}</div>}
    </div>
  );
};
